import {
  Button,
  FlexBox,
  JoonUIColor,
  Loader,
  SPACING,
  TextArea,
  Typography,
} from "@joonapp/web-shared"
import { useState } from "react"

import {
  trackGenerationCompleted,
  trackGenerationFailed,
  trackGenerationStarted,
} from "./sharedTools"
import ToolPage from "./ToolPage"
import {
  DataCollectionRecommendation as Recommendation,
  generateDataCollectionRecommendations,
} from "../../networking/tools"
import { requireAuth } from "../../util/auth"

const TOOL_ID = "data_collection_helper"

const DataCollectionHelper = requireAuth(() => {
  const [problem, setProblem] = useState("")
  const [recommendations, setRecommendations] = useState<Recommendation[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const generate = async () => {
    setLoading(true)
    setError(null)
    const t0 = Date.now()
    trackGenerationStarted(TOOL_ID, { problem_length: problem.length })
    try {
      const data = await generateDataCollectionRecommendations(problem)
      setRecommendations(data.recommendations)
      trackGenerationCompleted(TOOL_ID, { duration_ms: Date.now() - t0 })
    } catch (e: any) {
      setError("Failed to generate recommendations. Please try again.")
      trackGenerationFailed(TOOL_ID, { error_code: e.message })
    }
    setLoading(false)
  }

  return (
    <ToolPage
      toolId={TOOL_ID}
      title="Data Collection Helper"
      description="Describe a behavior or learning context. We'll recommend appropriate data collection methods with explanations."
    >
      <FlexBox direction="column" gap={SPACING.space1}>
        <Typography variant="bodyBold" textAlign="left">
          Behavior problem or context
        </Typography>
        <TextArea
          name="Behavior problem"
          value={problem}
          onChange={(e) => setProblem(e.target.value)}
          placeholder="e.g., Frequent tantrums during homework time, or, Need to track progress on using a communication device"
          fullWidth
        />
      </FlexBox>
      <Button
        text="Get recommendations"
        onClick={generate}
        disabled={loading || !problem}
        fullWidth
      />
      {loading && (
        <FlexBox justify="center">
          <Loader size={24} color={JoonUIColor.semantic.primary} />
        </FlexBox>
      )}
      {error && (
        <Typography variant="bodySmall" color={JoonUIColor.semantic.alert}>
          {error}
        </Typography>
      )}
      {recommendations.length > 0 && (
        <FlexBox direction="column" gap={SPACING.space3}>
          <Typography variant="h3" textAlign="left">
            Recommended data collection methods
          </Typography>
          {recommendations.map((r, i) => (
            <FlexBox
              key={i}
              direction="column"
              gap={SPACING.space1}
              style={{
                border: `1px solid ${JoonUIColor.border.default}`,
                borderRadius: SPACING.space2,
                padding: SPACING.space4,
              }}
            >
              <Typography variant="bodyBold" textAlign="left">
                {r.method}
              </Typography>
              <Typography variant="bodySmall" textAlign="left">
                <strong>Description:</strong> {r.description}
              </Typography>
              <Typography variant="bodySmall" textAlign="left">
                <strong>Why it's suitable:</strong> {r.suitability}
              </Typography>
            </FlexBox>
          ))}
        </FlexBox>
      )}
    </ToolPage>
  )
})

export default DataCollectionHelper
